import { addDays, addMinutes, format, parseISO, startOfWeek } from "date-fns";
import { useCommunication } from "./CommunicationWrapper";

interface GoogleCalendarEvent {
    id?: string;
    summary: string;
    description?: string;
    start: {
        dateTime?: string;
        date?: string;
        timeZone?: string;
    };
    end: {
        dateTime?: string;
        date?: string;
        timeZone?: string;
    };
    colorId?: string;
    extendedProperties?: {
        private: {
            canvasId?: string;
            canvasType?: string;
        };
    };
}

type TodoItem = {
    id: string;
    title: string;
    due: string;
    course: string;
    points: string;
    url: string;
}

/*
export const createGoogleEvent = async (calendarId: string, event: GoogleCalendarEvent) => {

    const validToken = await checkAndRefreshToken();
    if (!validToken) {
        // showError("Google Calendar login expired, please login again", "warning");
        return;
    }
    const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events`, {
        method: "POST",
        headers: {
            Authorization: `Bearer ${validToken.access_token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(event),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error(`[GoogleCalPush] Failed to create event. Status: ${response.status}, Error:`, errorText);
        throw new Error(`Failed to create event: ${response.statusText} - ${errorText}`);
    }

    return await response.json();
};

export const updateGoogleEvent = async (calendarId: string, eventId: string, event: GoogleCalendarEvent) => {


    const validToken = await checkAndRefreshToken();
    if (!validToken) {
        return;
    }
    const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events/${eventId}`, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${validToken.access_token}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(event),
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error(`[GoogleCalPush] Failed to update event. Status: ${response.status}, Error:`, errorText);
        throw new Error(`Failed to update event: ${response.statusText} - ${errorText}`);
    }

    return await response.json();
};

export const deleteGoogleEvent = async (calendarId: string, eventId: string) => {

    const validToken = await checkAndRefreshToken();
    if (!validToken) {
        return;
    }
    try {
        const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/${calendarId}/events/${eventId}`, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${validToken.access_token}`,
            },
        });

        if (!response.ok && response.status !== 404) {
            throw new Error(`Failed to delete event: ${response.statusText}`);
        }
    } catch (e) {
        console.error("[GoogleCalPush] Failed to delete event", e);
    }
};
*/

// Add these constants at the top
const BETTERCAMPUS_CALENDAR_NAME = "BetterCampus Calendar";
const BETTERCAMPUS_CALENDAR_DESCRIPTION = "Automatically synced events from BetterCampus";
const BETTERCAMPUS_CALENDAR_COLOR = "#4285f4"; // Google Blue

type EventOperation = {
    event: GoogleCalendarEvent | null,
    op: "create" | "update" | "delete",
    calendar: string
}

export const useGoogleCalendarSync = () => {

    const { updateOptions, getValidCalendarToken, getOptions, origin, sendMessage, sendMessageAndListen, sendToBackgroundScript } = useCommunication();

    const addToUnsyncedQueue = async (events: Record<string, EventOperation>) => {
        try {
            sendToBackgroundScript({
                type: "add_to_calendar_queue",
                events: events
            });
        } catch (err) {
            console.error("[GoogleCalPush] Failed to send events to background queue", err);
        }
    };

    const handleInitialDueDates = async () => {

        const settings = getOptions("sync");
        const targetCalendar = settings?.google_cal_push_settings?.betterCampusCalendarId || "";

        // Get current storage
        const localOptions = getOptions("local");
        const syncedCalendar = localOptions?.synced_calendar || {};
        const unsyncedCalendar = localOptions?.unsynced_calendar || [];

        // retrieve calendar events until we dont recieve back 100
        const promise = new Promise<TodoItem[]>(async (resolve, reject) => {

            const params = new URLSearchParams();
            const ids = getOptions("sync")?.card_ids || [];
            ids.forEach((id: string) => params.append("context_codes[]", "course_" + id));

            const now = new Date().toISOString();
            const end = addDays(new Date(), 130).toISOString();
            let infiniteLoopBackoff = 1;
            let retrieved = 100;
            let assignments: TodoItem[] = [];

            while (retrieved === 100 && infiniteLoopBackoff < 30) {

                const endpoint = `/api/v1/calendar_events?${params.toString()}&type=assignment&start_date=${now}&end_date=${end}&page=${infiniteLoopBackoff}&per_page=100`;

                try {
                    const res = await sendMessageAndListen({
                        type: "REQUEST_CANVAS_DATA",
                        payload: {
                            endpoint: endpoint,
                        }
                    });

                    console.log("[message response]", res);

                    const data = res?.data || [];
                    retrieved = data?.length || 0;
                    infiniteLoopBackoff++;

                    const items: TodoItem[] = data.map((item: any) => ({
                        id: item?.assignment?.id,
                        title: item?.assignment?.name,
                        due: item?.assignment?.due_at,
                        course: item?.assignment?.course_id,
                        points: item?.assignment?.points_possible,
                        url: item?.assignment?.html_url,
                    }));

                    assignments = assignments.concat(items);

                    console.log("[assignments]", assignments);

                    if (retrieved !== 100) resolve(assignments);
                    if (infiniteLoopBackoff >= 30) reject(new Error("Failed to fetch calendar events"));

                } catch (err) {
                    console.error("Error fetching calendar events", err);
                }
            }
        });

        const assignments = await promise;

        console.log("[assignments complete]", assignments);

        // Filter out assignments that are already synced or in unsynced queue
        const newAssignments = assignments.filter((assignment: TodoItem) => {
            const canvasId = `todo_${assignment.id}`;

            // Skip if already synced
            if (syncedCalendar[canvasId]) {
                console.log(`[CalendarSync] Assignment ${assignment.id} already synced, skipping`);
                return false;
            }

            // Skip if already in unsynced queue
            if (unsyncedCalendar.some((item: any) => item.canvasId === canvasId)) {
                console.log(`[CalendarSync] Assignment ${assignment.id} already in queue, skipping`);
                return false;
            }

            return true;
        });

        // Add to unsynced_calendar array in new format
        if (newAssignments.length > 0) {
            const assignmentsToAdd = newAssignments.map((assignment: TodoItem) => ({
                canvasId: `todo_${assignment.id}`,
                operation: 'insert',
                eventData: {
                    title: assignment.title,
                    startDateTime: assignment.due,
                    duration: undefined, // all-day event
                    description: `Course: ${assignment.course}\nPoints: ${assignment.points}`,
                    type: 'due'
                }
            }));

            const updatedUnsynced = [...unsyncedCalendar, ...assignmentsToAdd];

            updateOptions({
                unsynced_calendar: updatedUnsynced
            }, "local");

            console.log(`[CalendarSync] Added ${newAssignments.length} assignments to unsynced queue`);
        } else {
            console.log("[CalendarSync] No new assignments to add to queue");
        }

    }

    const handleInitialPlanned = async () => {

        const settings = await storage.getItem('sync:google_cal_push_settings') as any;
        const targetCalendar = settings?.betterCampusCalendarId || "";

        const options = getOptions("sync");
        const cards = (options?.card_ids || []).map((id: string) => "card_" + id);

        const events: Record<string, EventOperation> = {};

        Object.keys(storage.planned || {}).forEach((key) => {
            try {

                const details = storage.planned[key];
                const plannedAt = details.plannedAt;
                if (!details.plannedAt || !details.duration) {
                    return;
                }

                const canvasId = `planned_${details.id}`;
                const courseData = cards["card_" + (details?.course || "")];

                const date = new Date(plannedAt);
                const start = new Date(date);
                const end = addMinutes(start, details.duration);

                // Check if the planned date is in the past
                if (start.getTime() < Date.now()) {
                    return;
                }

                if (start.getTime() > end.getTime()) {
                    console.error("google sync planned date is in the past somehow", details);
                    return;
                }

                const event: GoogleCalendarEvent = {
                    summary: `[WORK] ${details.title}`,
                    description: `Course: ${courseData?.code || courseData?.default}\nPoints: ${details.points || "N/A"}\nLink: ${details.url}`,
                    start: {
                        dateTime: start.toISOString(),
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                    end: {
                        dateTime: end.toISOString(),
                        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    },
                    //colorId: "11",
                    extendedProperties: {
                        private: {
                            canvasId: canvasId,
                            canvasType: "assignment",
                        },
                    },
                };

                events[canvasId] = { "event": event, "op": "create", "calendar": targetCalendar } as EventOperation;
            } catch (err) {
                console.error("Error queueing planned item:", err);
            }
        });

        await addToUnsyncedQueue(events);


        try {
            //await chrome.runtime.sendMessage({ type: "google_cal_process_queue" });
        } catch (e) {
            console.warn("Failed to trigger background queue processing", e);
        }
    }

    const handleInitialClassTimes = async () => {
        const options = getOptions("sync");
        const settings = options?.google_cal_push_settings || {};
        const targetCalendar = settings?.betterCampusCalendarId || "";

        if (!targetCalendar) return;


        const cards = (options?.card_ids || []).map((id: string) => "card_" + id);

        const events: Record<string, EventOperation> = {};
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        for (const courseId of storage.card_ids) {
            const course = cards["card_" + courseId];
            const typedCourse = course as any;
            const schedule = typedCourse?.schedule || [];

            if (schedule.length === 0) continue;


            for (const period of schedule) {
                try {
                    // Normalize start/end times
                    const start = Math.min(period.start, period.end);
                    const end = Math.max(period.start, period.end);

                    // Skip if start and end are the same
                    if (start === end) {
                        console.warn(`[GoogleSync] Skipping class time with equal start/end: ${courseId}, day ${period.day}`);
                        continue;
                    }

                    const canvasId = `class_${courseId}_${period.day}_${start}`;

                    // Get start of current week and add the day offset
                    const weekStart = startOfWeek(new Date(), { weekStartsOn: 0 });
                    const nextClass = new Date(weekStart);
                    nextClass.setDate(weekStart.getDate() + period.day);
                    nextClass.setHours(Math.floor(start / 60), start % 60, 0, 0);

                    const endTime = new Date(nextClass);
                    endTime.setHours(Math.floor(end / 60), end % 60, 0, 0);

                    const event: GoogleCalendarEvent = {
                        summary: typedCourse.code || typedCourse.default || `Course ${courseId}`,
                        description: `Class session for ${typedCourse.default || typedCourse.code || courseId}`,
                        start: {
                            dateTime: nextClass.toISOString(),
                            timeZone: timeZone,
                        },
                        end: {
                            dateTime: endTime.toISOString(),
                            timeZone: timeZone,
                        },
                        //colorId: "9", // Blue for classes
                        extendedProperties: {
                            private: {
                                canvasId: canvasId,
                                canvasType: "class",
                            },
                        },
                    };

                    // Only add recurrence for new events
                    (event as any).recurrence = ["RRULE:FREQ=WEEKLY"];

                    events[canvasId] = { "event": event, "op": "create", "calendar": targetCalendar } as EventOperation;
                } catch (err) {
                    console.error("Error queueing class time:", err);
                }
            }
        }

        await addToUnsyncedQueue(events);


        try {
            //await chrome.runtime.sendMessage({ type: "google_cal_process_queue" });
        } catch (e) {
            console.warn("Failed to trigger background queue processing", e);
        }
    }


    const handleInitialGoogleSync = async () => {

        await handleInitialDueDates();
        await handleInitialPlanned();
        await handleInitialClassTimes();


    }

    const deleteGoogleCalendar = async () => {

        const settings = getOptions("sync");
        const betterCampusCalendarId = settings?.google_cal_push_settings?.betterCampusCalendarId || "";

        if (!betterCampusCalendarId) return;

        try {
            // Delete the BetterCampus calendar using Google API
            const validToken = await getValidCalendarToken();
            if (validToken) {
                const response = await fetch(`https://www.googleapis.com/calendar/v3/calendars/${betterCampusCalendarId}`, {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${validToken.access_token}`,
                    },
                });

                if (response.ok) {
                    console.log('[GoogleCal] Successfully deleted BetterCampus calendar:', betterCampusCalendarId);
                } else {
                    console.warn('[GoogleCal] Failed to delete calendar, status:', response.status);
                }
            }
        } catch (error) {
            console.error('[GoogleCal] Error deleting calendar:', error);
        }

    }

    const listGoogleCalendars = async () => {
        const validToken = await getValidCalendarToken();
        if (!validToken) return;

        const response = await fetch('https://www.googleapis.com/calendar/v3/users/me/calendarList', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${validToken.access_token}`,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            console.error('[GoogleCal] Failed to fetch calendars:', response.status, response.statusText);
            return;
        }

        const data = await response.json();
        const current = getOptions("sync").google_calendars || {};
        const output: Record<string, any> = { ...(current.google_calendars || {}) };

        // Track which calendars still exist
        const existingCalendarIds = new Set<string>();

        // Check if our BetterCampus calendar still exists
        const settings = getOptions("sync").google_cal_push_settings || {};
        const betterCampusCalendarId = settings?.betterCampusCalendarId || "";
        let betterCampusCalendarExists = false;

        (data.items || []).forEach((calendar: any) => {
            existingCalendarIds.add(calendar.id);

            // Check if this is our BetterCampus calendar
            if (betterCampusCalendarId && calendar.id === betterCampusCalendarId) {
                return;
            }

            console.log("calendar two here", calendar, current[calendar.id]);

            if (output[calendar.id]) {
                // Update existing calendar info
                output[calendar.id] = {
                    ...output[calendar.id],
                    summary: calendar.summary,
                    backgroundColor: calendar.backgroundColor,
                    lastSeen: Date.now(),
                };
            } else {
                // Add new calendar
                output[calendar.id] = {
                    id: calendar.id,
                    summary: calendar.summary,
                    backgroundColor: calendar.backgroundColor,
                    shouldSync: false,
                    lastSeen: Date.now(),
                };
            }
        });

        // Mark calendars as missing if they weren't in the latest fetch
        Object.keys(output).forEach(calendarId => {
            if (!existingCalendarIds.has(calendarId)) {
                output[calendarId] = {
                    ...output[calendarId],
                    missing: true,
                    lastSeen: output[calendarId].lastSeen || Date.now(),
                };
            }
        });

        updateOptions({
            google_calendars: output,
            google_cal_last_sync: Date.now(),
        }, "sync");

    }

    const resetGoogleCalendar = async () => {
        const settings = getOptions("sync").google_cal_push_settings || {};
        await deleteGoogleCalendar();
        await updateOptions({
            "google_cal_push_settings": {
                ...settings,
                betterCampusCalendarId: null,
            },
        }, "sync");
        await updateOptions({ google_cal_pushed_events: {}, synced_calendar: {}, unsynced_calendar: [] }, "local");
    }

    const disconnectGoogleCalendar = async () => {
        await deleteGoogleCalendar();
        await updateOptions({
            "google_cal_sync_token": null,
            "google_cal_push_settings": {
                betterCampusCalendarId: null,
                pushEnabled: false,
            },
            "google_cal_last_sync": null,
            "google_calendars": {}
        }, "sync");
        await updateOptions({ google_cal_pushed_events: {}, synced_calendar: {}, unsynced_calendar: [] }, "local");
    }

    // Add this function to create the BetterCampus calendar
    const createBetterCampusCalendar = async (): Promise<string | null> => {
        const validToken = await getValidCalendarToken();

        console.log("validToken", validToken);

        if (!validToken) return null;

        try {
            const response = await fetch('https://www.googleapis.com/calendar/v3/calendars', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${validToken.access_token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    summary: BETTERCAMPUS_CALENDAR_NAME,
                    description: BETTERCAMPUS_CALENDAR_DESCRIPTION,
                    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                    colorId: BETTERCAMPUS_CALENDAR_COLOR,
                }),
            });

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`[GoogleCal] Failed to create calendar. Status: ${response.status}, Error:`, errorText);
                return null;
            }

            const calendar = await response.json();
            console.log('[GoogleCal] Created BetterCampus calendar:', calendar.id);

            updateOptions({
                google_cal_push_settings: {
                    ...(getOptions("sync").google_cal_push_settings || {}),
                    betterCampusCalendarId: calendar.id,
                }
            }, "sync");
        } catch (error) {
            console.error('[GoogleCal] Error creating calendar:', error);
            return null;
        }
    };

    // Add this function to check if BetterCampus calendar exists
    const ensureBetterCampusGoogleCalendar = async (): Promise<string | null> => {
        const options = getOptions();
        const settings = options.google_cal_push_settings;

        if (settings?.betterCampusCalendarId) {
            return settings.betterCampusCalendarId;
        }

        // Calendar doesn't exist or is invalid, create a new one
        const newCalendarId = await createBetterCampusCalendar();

        if (newCalendarId) {
            // Update settings with new calendar ID
            updateOptions({
                google_cal_push_settings: {
                    ...settings,
                    betterCampusCalendarId: newCalendarId,
                }
            }, "sync");
        }

        return newCalendarId;
    };

    return {
        handleInitialGoogleSync,
        deleteGoogleCalendar,
        disconnectGoogleCalendar,
        resetGoogleCalendar,
        listGoogleCalendars,
        ensureBetterCampusGoogleCalendar,
    }

}